import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  SuppliersPageVendorPromotionListDocument,
  useSuppliersPageVendorPromotionListQuery,
  type SuppliersPageVendorPromotionListQuery,
  type SuppliersPageVendorPromotionListQueryVariables,
  type VendorPromotionListInput,
} from '../../../../../../../generated';
import { type PromotionListResult, type PromotionRow } from '../types';
import { mapVendorPromotionResponse } from '../utils/vendorPromotionMapper';

const PAGE_SIZE = 25;

interface UseVendorPromotionListParams {
  vendorId?: string;
  /** When true, only active promotions are requested. */
  activeOnly: boolean;
}

interface UseVendorPromotionListResult {
  promotions: PromotionRow[];
  /** First page is loading, so there is still an empty table. */
  isLoading: boolean;
  /** A "Load more" page is loading. */
  isLoadingMore: boolean;
  errorMessage: string | null;
  /** A "Load more" page failed; the rows already loaded stay on screen. */
  loadMoreErrorMessage: string | null;
  hasNextPage: boolean;
  loadMore: () => void;
  /** Swaps one loaded row for a freshly saved version of it. */
  replacePromotion: (promotion: PromotionRow) => void;
}

const LOAD_MORE_ERROR_MESSAGE =
  'Could not load more promotions. Press Load more to try again.';

/**
 * Read-only list of a distributor's promotions, accumulating cursor pages for
 * a "Load more" button.
 */
export const useVendorPromotionList = ({
  vendorId,
  activeOnly,
}: UseVendorPromotionListParams): UseVendorPromotionListResult => {
  const client = useApolloClient();
  const enabled = Boolean(vendorId);

  const buildInput = useCallback(
    (cursor?: string): VendorPromotionListInput => ({
      vendorId: vendorId ?? '',
      // With the toggle off no `isActive` filter is sent, so the list shows
      // active and inactive promotions together.
      filter: activeOnly ? { isActive: true } : undefined,
      pagination: { limit: PAGE_SIZE, direction: 'forward', cursor },
    }),
    [vendorId, activeOnly]
  );

  const query = useSuppliersPageVendorPromotionListQuery({
    variables: { input: buildInput() },
    skip: !enabled,
    // no-cache: the VendorPromotionListResponse union members have no `id`,
    // so InMemoryCache cannot normalise them and a cache read drops the
    // inline-fragment `edges`. Reading from the network keeps the response
    // intact, and "Load more" below reads the same way.
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const mapped = useMemo(
    () => mapVendorPromotionResponse(query.data?.vendorPromotionList),
    [query.data]
  );

  const signature = `${vendorId ?? ''}::${activeOnly ? 'active' : 'all'}`;

  const [promotions, setPromotions] = useState<PromotionRow[]>([]);
  const [pageInfo, setPageInfo] = useState<{
    hasNextPage: boolean;
    endCursor: string;
  } | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreErrorMessage, setLoadMoreErrorMessage] = useState<
    string | null
  >(null);
  const seededSignature = useRef<string | null>(null);
  const seededResult = useRef<PromotionListResult | null>(null);
  // Bumped on every reset and read by an in-flight "Load more". A signature
  // comparison would let a request survive a toggle away and back, because the
  // signature it started with matches again by the time it lands.
  const generation = useRef(0);

  // Drop the accumulated pages whenever the filter changes. Clearing
  // `isLoadingMore` keeps the button from spinning after a toggle mid-request.
  useEffect(() => {
    generation.current += 1;
    seededSignature.current = null;
    seededResult.current = null;
    setPromotions([]);
    setPageInfo(null);
    setIsLoadingMore(false);
    setLoadMoreErrorMessage(null);
  }, [signature]);

  // (Re)seed page one on a fresh network completion. Gating on `!loading`
  // ignores Apollo's synchronous same-variable replay, so the list does not
  // latch on a stale first page.
  useEffect(() => {
    if (query.loading || mapped?.kind !== 'success') {
      return;
    }
    if (
      seededSignature.current === signature &&
      seededResult.current === mapped
    ) {
      return;
    }
    seededSignature.current = signature;
    seededResult.current = mapped;
    setPromotions(mapped.page.promotions);
    setPageInfo(mapped.page.pageInfo);
  }, [mapped, signature, query.loading]);

  const loadMore = useCallback(() => {
    if (
      !enabled ||
      !pageInfo?.hasNextPage ||
      !pageInfo.endCursor ||
      isLoadingMore
    ) {
      return;
    }
    const cursor = pageInfo.endCursor;
    const requestGeneration = generation.current;
    setIsLoadingMore(true);
    setLoadMoreErrorMessage(null);

    client
      .query<
        SuppliersPageVendorPromotionListQuery,
        SuppliersPageVendorPromotionListQueryVariables
      >({
        query: SuppliersPageVendorPromotionListDocument,
        variables: { input: buildInput(cursor) },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .then((response) => {
        // A response that lands after a filter change belongs to the previous
        // list, so appending it would poison the rows and the cursor.
        if (requestGeneration !== generation.current) {
          return;
        }
        const nextPage = mapVendorPromotionResponse(
          response.data?.vendorPromotionList
        );
        if (nextPage?.kind === 'success') {
          setPromotions((previous) => [
            ...previous,
            ...nextPage.page.promotions,
          ]);
          setPageInfo(nextPage.page.pageInfo);
          return;
        }
        // A failure member on a later page keeps the loaded rows and says why
        // the rest did not arrive.
        setLoadMoreErrorMessage(nextPage?.message ?? LOAD_MORE_ERROR_MESSAGE);
      })
      .catch(() => {
        if (requestGeneration === generation.current) {
          setLoadMoreErrorMessage(LOAD_MORE_ERROR_MESSAGE);
        }
      })
      .finally(() => {
        if (requestGeneration === generation.current) {
          setIsLoadingMore(false);
        }
      });
  }, [enabled, pageInfo, isLoadingMore, client, buildInput]);

  const replacePromotion = useCallback((promotion: PromotionRow) => {
    setPromotions((previous) =>
      previous.map((row) => (row.id === promotion.id ? promotion : row))
    );
  }, []);

  const errorMessage =
    mapped?.kind === 'error' ? mapped.message : (query.error?.message ?? null);

  return {
    promotions,
    isLoading:
      enabled && query.loading && promotions.length === 0 && !errorMessage,
    isLoadingMore,
    errorMessage,
    loadMoreErrorMessage,
    hasNextPage: Boolean(pageInfo?.hasNextPage),
    loadMore,
    replacePromotion,
  };
};
